import { Icon } from '@natera/material/lib/icon'
import { Fragment, useEffect, useMemo, useState } from 'react'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { cn } from '~/shared/lib/cn'
import { formatDateDefault } from '~/shared/lib/datetime'
import { Button, Chip, IconButton, InfoTip, LocalIcon, RadioButton, Text } from '~/shared/ui'
import { NO_DATA_PLACEHOLDER } from '../../config'
import type { PatientMatch } from '../../model'
import {
  CardDivider,
  ContradictionChip,
  FieldRow,
  VendorField,
  type FieldStatus,
} from './pa-card-shared'
import { PaMatchCard } from './pa-match-card'

const CARD_MIN_WIDTH = 240

export function PaMatchesSection({
  data,
  containerWidth,
  selectable = false,
  selectedIndex,
  onSelect,
  onAddNewMatch,
  newlyAddedMatch,
  onRemoveNewMatch,
  documentFields,
  onClearSelection,
  showContradictions = false,
  hoveredField = null,
  isPaCardRedesignEnabled = false,
  connectUrlMap,
  isMatchingV2 = false,
}: {
  data: apiClient.PatientPaCaseCandidatesResponse
  containerWidth: number
  selectable?: boolean
  selectedIndex?: number
  onSelect?: (index: number) => void
  onAddNewMatch?: () => void
  newlyAddedMatch?: PatientMatch | null
  onRemoveNewMatch?: () => void
  documentFields?: Record<string, string | undefined>
  onClearSelection?: () => void
  showContradictions?: boolean
  isPaCardRedesignEnabled?: boolean
  hoveredField?: string | null
  connectUrlMap?: Map<string, string>
  isMatchingV2?: boolean
}) {
  const sortedPatients = useMemo(() => {
    const raw = data.patient_data ?? []
    const indexed = raw.map((record, originalIndex) => ({ record, originalIndex }))
    const selected = data.selected_match_index
    if (selected == null) return indexed
    return [...indexed].sort((a, b) => {
      if (a.originalIndex === selected) return -1
      if (b.originalIndex === selected) return 1
      return 0
    })
  }, [data.patient_data, data.selected_match_index])
  const [isExpanded, setIsExpanded] = useState(false)
  const [hiddenIndexes, setHiddenIndexes] = useState<Set<number>>(new Set())
  const [fadingOutIndex, setFadingOutIndex] = useState<number | null>(null)
  const [currentPage, setCurrentPage] = useState(0)
  const visiblePatients = sortedPatients.filter((p) => !hiddenIndexes.has(p.originalIndex))
  const totalCount = sortedPatients.length

  const MAX_CARDS = 3
  const CARD_GAP = 12
  const ADD_NEW_WIDTH = 132
  const PADDING = 64
  const hasAddButton = !!onAddNewMatch
  const addButtonSpace = hasAddButton ? ADD_NEW_WIDTH + CARD_GAP : 0
  const hasNewlyAdded = !!newlyAddedMatch
  const newlyAddedSpace = hasNewlyAdded ? CARD_MIN_WIDTH + CARD_GAP : 0
  const availableForCards = (containerWidth || 1280) - PADDING - addButtonSpace - newlyAddedSpace
  const cardsPerPage = Math.min(
    MAX_CARDS,
    Math.max(1, Math.floor((availableForCards + CARD_GAP) / (CARD_MIN_WIDTH + CARD_GAP))),
  )
  const totalSlots = cardsPerPage + (hasNewlyAdded ? 1 : 0)
  const cardWidth = Math.floor(
    ((containerWidth || 1280) - PADDING - addButtonSpace - (totalSlots - 1) * CARD_GAP) /
      totalSlots,
  )

  const totalPages = Math.ceil(visiblePatients.length / cardsPerPage)
  const page = Math.min(currentPage, Math.max(0, totalPages - 1))
  if (currentPage !== page && currentPage > 0) {
    setTimeout(() => setCurrentPage(page), 0)
  }
  const paginatedPatients = visiblePatients.slice(page * cardsPerPage, (page + 1) * cardsPerPage)

  if (totalCount === 0 && !newlyAddedMatch) return null

  const showCards = selectable || isExpanded

  return (
    <div className={selectable ? 'pt-4 pb-4' : 'border-divider border-t pt-4 pb-4'}>
      {selectable ? (
        <div className="flex items-center justify-between gap-4 px-8">
          <div className="flex items-center gap-2">
            <Text appearance="H6">Select a Match</Text>
            <InfoTip
              iconSize="24"
              align="start"
            >
              <div className="flex w-[288px] flex-col gap-2 pr-6">
                <Text appearance="SUBTITLE2">Matching Legend</Text>
                <Text
                  className="text-text-secondary"
                  appearance="CAPTION"
                >
                  Opti searched Natera's systems and pulled these potential matches. Select the
                  relevant match.
                </Text>
                <div className="border-divider border-b" />
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Icon
                      className="text-chip-status-success-content shrink-0 text-[20px]"
                      children="check"
                    />
                    <Text appearance="CAPTION">Matches the document</Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon
                      className="text-alert-error-message shrink-0 text-[20px]"
                      children="close"
                    />
                    <Text appearance="CAPTION">Contradicts the document</Text>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon
                      className="text-text-secondary shrink-0 text-[20px]"
                      children="remove"
                    />
                    <Text appearance="CAPTION">No data</Text>
                  </div>
                </div>
              </div>
            </InfoTip>
          </div>
          <div className="flex items-center gap-4">
            <Text appearance="BODY2">
              {page * cardsPerPage + 1} –{' '}
              {Math.min((page + 1) * cardsPerPage, visiblePatients.length)} of{' '}
              {visiblePatients.length}
            </Text>
            <IconButton
              size="medium"
              container="small"
              materialIcon="first_page"
              onClick={() => setCurrentPage(0)}
              disabled={page === 0}
              aria-label="First page"
            />
            <IconButton
              size="medium"
              container="small"
              materialIcon="chevron_left"
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              aria-label="Previous page"
            />
            <IconButton
              size="medium"
              container="small"
              materialIcon="chevron_right"
              onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              aria-label="Next page"
            />
            <IconButton
              size="medium"
              container="small"
              materialIcon="last_page"
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={page >= totalPages - 1}
              aria-label="Last page"
            />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-8">
          <Button
            size="small"
            materialIcon={isExpanded ? 'expand_less' : 'expand_more'}
            onClick={() => setIsExpanded((v) => !v)}
          >
            PA Case Matches ({totalCount})
          </Button>
          {data.system_match_method && (
            <Text
              className="text-text-secondary"
              appearance="CAPTION"
            >
              Method: {data.system_match_method}
            </Text>
          )}
          {data.query && <QuerySummary query={data.query} />}
        </div>
      )}

      {showCards && (
        <div
          className="mt-3 px-8 pb-2"
          style={{ width: containerWidth || '100%' }}
        >
          <div className="flex items-stretch gap-3">
            {newlyAddedMatch && (
              <div
                className="shrink-0"
                style={{ width: `${cardWidth}px` }}
              >
                <NewlyAddedMatchCard
                  match={newlyAddedMatch}
                  isSelected={selectedIndex === newlyAddedMatch.matchIndex}
                  onSelect={() => onSelect?.(newlyAddedMatch.matchIndex)}
                  onRemove={onRemoveNewMatch}
                  documentFields={documentFields}
                  showContradictions={showContradictions}
                  hoveredField={hoveredField}
                />
              </div>
            )}
            {paginatedPatients.map(({ record, originalIndex }) => {
              const isFadingOut = fadingOutIndex === originalIndex
              return (
                <div
                  key={originalIndex}
                  className={cn(
                    'shrink-0 transition-opacity duration-300',
                    isFadingOut && 'pointer-events-none opacity-0',
                  )}
                  style={{ width: `${cardWidth}px` }}
                >
                  <PaMatchCard
                    record={record}
                    isSystemSelected={!selectable && data.selected_match_index === originalIndex}
                    isSelectable={selectable}
                    isSelected={selectable && selectedIndex === originalIndex}
                    onSelect={selectable ? () => onSelect?.(originalIndex) : undefined}
                    canHide={isPaCardRedesignEnabled && visiblePatients.length > 1}
                    showContradictions={showContradictions}
                    hoveredField={hoveredField}
                    documentFields={documentFields}
                    isUserCreated={
                      isMatchingV2 &&
                      !record.similarity &&
                      record.similarity !== 0 &&
                      !record.matched_on?.length &&
                      !record.contradicted_on?.length
                    }
                    isMatchingV2={isMatchingV2}
                    connectUrl={
                      (record.case_id && connectUrlMap?.get(record.case_id)) ||
                      (record.care_id && connectUrlMap?.get(record.care_id)) ||
                      undefined
                    }
                    onHide={() => {
                      setFadingOutIndex(originalIndex)
                      setTimeout(() => {
                        if (selectedIndex === originalIndex) {
                          onClearSelection?.()
                        }
                        setHiddenIndexes((prev) => new Set([...prev, originalIndex]))
                        setFadingOutIndex(null)
                      }, 300)
                    }}
                  />
                </div>
              )
            })}
            {onAddNewMatch && (
              <button
                className={cn(
                  'border-divider bg-surface-lowest ml-auto flex shrink-0 cursor-pointer flex-col items-center justify-center rounded-lg border',
                  isMatchingV2 && 'px-4',
                )}
                type="button"
                style={{ width: `${ADD_NEW_WIDTH}px`, minWidth: `${ADD_NEW_WIDTH}px` }}
                onClick={onAddNewMatch}
              >
                <Icon
                  className="text-text-link text-[18px]"
                  children="add"
                />
                <span
                  className={cn(
                    'text-text-link mt-1 text-center leading-tight font-medium tracking-[1.25px] uppercase',
                    isMatchingV2 ? 'text-sm' : 'text-xs',
                  )}
                >
                  Add new match
                </span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function compareField(
  matchValue: string | null | undefined,
  extractedValue: string | undefined,
  isDate = false,
): FieldStatus {
  if (!matchValue || !extractedValue) return 'indeterminate'
  const a = isDate ? formatDateDefault(matchValue) : matchValue.trim().toLowerCase()
  const b = isDate ? formatDateDefault(extractedValue) : extractedValue.trim().toLowerCase()
  return a === b ? 'match' : 'contradiction'
}

const ADDED_CARD_FIELDS = [
  {
    key: 'date_of_birth',
    label: 'Birth Date',
    matchField: 'date_of_birth',
    extractedField: 'member_dob',
    isDate: true,
  },
  {
    key: 'payor_name',
    label: 'Payor Name',
    matchField: 'payorname',
    extractedField: 'insurance_company',
  },
  { key: 'test_name', label: 'Test Name', matchField: 'test_name', extractedField: 'test_name' },
  {
    key: 'date_of_service',
    label: 'Date of Service',
    matchField: 'lims_date_of_service',
    extractedField: 'service_date_from',
    isDate: true,
  },
  {
    key: 'member_id',
    label: 'Member ID',
    matchField: 'policy_number',
    extractedField: 'member_id',
  },
  {
    key: 'lims_case_id',
    label: 'LIMS Case ID',
    matchField: 'lims_casefile_id',
    extractedField: 'case_number',
  },
  {
    key: 'reference_number',
    label: 'Reference Number',
    matchField: 'reference_number',
    extractedField: 'reference_number',
  },
  {
    key: 'authorization_code',
    label: 'Authorization Code',
    matchField: 'authorization_code',
    extractedField: 'authorization_code',
  },
  {
    key: 'cpt_codes',
    label: 'CPT Codes',
    matchField: 'allproccodes_claim',
    extractedField: 'cpt_code',
  },
] as const

function NewlyAddedMatchCard({
  match,
  isSelected,
  onSelect,
  onRemove,
  documentFields,
  showContradictions = false,
  hoveredField = null,
}: {
  match: PatientMatch
  isSelected?: boolean
  onSelect?: () => void
  onRemove?: () => void
  documentFields?: Record<string, string | undefined>
  showContradictions?: boolean
  hoveredField?: string | null
}) {
  const [isContradictedExpanded, setIsContradictedExpanded] = useState(showContradictions)
  useEffect(() => {
    setIsContradictedExpanded(showContradictions)
  }, [showContradictions])

  const { fieldStatus, contradictedLabels } = useMemo(() => {
    if (!documentFields) {
      return {
        fieldStatus: (): FieldStatus => 'indeterminate',
        contradictedLabels: [] as string[],
      }
    }

    const statuses: Record<string, FieldStatus> = {}
    const contradicted: string[] = []

    for (const field of ADDED_CARD_FIELDS) {
      const { key, label, matchField, extractedField } = field
      const isDate = 'isDate' in field && field.isDate
      const matchValue = (match as unknown as Record<string, unknown>)[matchField] as
        | string
        | null
        | undefined
      const extractedValue = documentFields[extractedField]
      const result = compareField(matchValue, extractedValue, isDate)
      statuses[key] = result
      if (result === 'contradiction') contradicted.push(label)
    }

    return {
      fieldStatus: (key: string): FieldStatus => statuses[key] ?? 'indeterminate',
      contradictedLabels: contradicted,
    }
  }, [match, documentFields])

  return (
    <div
      className={cn(
        'bg-surface-lowest flex h-full flex-col overflow-hidden rounded-lg border-2 whitespace-nowrap',
        isSelected ? 'border-primary-main' : 'border-divider',
      )}
    >
      <div className="border-divider min-h-[52px] border-b">
        <button
          className="bg-surface-lowest relative flex h-full w-full cursor-pointer items-center justify-between border-none px-4 py-3"
          type="button"
          onClick={onSelect}
        >
          <RadioButton
            label="Added by user"
            withinButton
            checked={!!isSelected}
            onChange={() => onSelect?.()}
          />
        </button>
      </div>

      <div className="px-4 pt-2">
        <Text appearance="OVERLINE">Natera Patient Data</Text>
      </div>

      <div className="flex flex-col gap-2 px-4 py-3">
        <Text
          appearance="SUBTITLE"
          placeholder={NO_DATA_PLACEHOLDER}
          truncated
          withTooltip
          copyable
        >
          {match.patient_first_name} {match.patient_last_name}
        </Text>
        <FieldRow
          label="Birth Date"
          value={match.date_of_birth ? formatDateDefault(match.date_of_birth) : null}
          status={fieldStatus('date_of_birth')}
          hoveredField={hoveredField}
          hoveredFieldKey="birth_date"
          extractedValue={documentFields?.member_dob}
          isDate
        />
        <FieldRow
          label="Payor Name"
          value={match.payorname}
          status={fieldStatus('payor_name')}
          hoveredField={hoveredField}
          hoveredFieldKey="insurance_company"
          extractedValue={documentFields?.insurance_company}
        />
        <FieldRow
          label="Test Name"
          value={match.test_name}
          status={fieldStatus('test_name')}
          hoveredField={hoveredField}
          hoveredFieldKey="test_name"
          extractedValue={documentFields?.test_name}
        />
        <FieldRow
          label="Date of Service"
          value={match.lims_date_of_service ? formatDateDefault(match.lims_date_of_service) : null}
          status={fieldStatus('date_of_service')}
          hoveredField={hoveredField}
          hoveredFieldKey="date_of_service"
          extractedValue={documentFields?.service_date_from}
          isDate
        />
      </div>

      <CardDivider />

      <div className="flex flex-col gap-3 px-4 py-3">
        <FieldRow
          label="Member ID"
          value={match.policy_number}
          status={fieldStatus('member_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="member_id"
          extractedValue={documentFields?.member_id}
        />
        <FieldRow
          label="LIMS Case ID"
          value={match.lims_casefile_id}
          status={fieldStatus('lims_case_id')}
          hoveredField={hoveredField}
          hoveredFieldKey="case_number"
          extractedValue={documentFields?.case_number}
        />
        <FieldRow
          label="Reference Number"
          value={match.reference_number}
          status={fieldStatus('reference_number')}
          hoveredField={hoveredField}
          hoveredFieldKey="reference_number"
          extractedValue={documentFields?.reference_number}
        />
        <FieldRow
          label="Authorization Code"
          value={match.authorization_code}
          status={fieldStatus('authorization_code')}
        />
        <FieldRow
          label="CPT Codes"
          value={match.allproccodes_claim?.replace(/;/g, ', ')}
          status={fieldStatus('cpt_codes')}
          hoveredField={hoveredField}
          hoveredFieldKey="cpt_code"
          extractedValue={documentFields?.cpt_code}
        />
      </div>

      <CardDivider />

      <div className="flex flex-col gap-3 px-4 py-3">
        <VendorField
          label="Prior Auth Vendor"
          value={match.pa_vendor}
        />
      </div>

      <CardDivider />

      <div className="flex flex-col">
        <button
          className="flex w-full cursor-pointer items-center gap-2 border-none bg-transparent px-4 pt-4 text-left"
          type="button"
          onClick={() => setIsContradictedExpanded((v) => !v)}
        >
          <Text
            className="flex-1"
            appearance="OVERLINE"
          >
            Contradicted on{isContradictedExpanded ? '' : ` (${contradictedLabels.length})`}
          </Text>
          <Icon
            className="text-[20px]"
            children={isContradictedExpanded ? 'expand_less' : 'expand_more'}
          />
        </button>
        {isContradictedExpanded && (
          <div className="flex flex-wrap gap-1 px-4 py-3 whitespace-normal">
            {contradictedLabels.length > 0 ? (
              contradictedLabels.map((label) => (
                <ContradictionChip key={label}>{label}</ContradictionChip>
              ))
            ) : (
              <Text
                className="text-text-secondary"
                appearance="BODY2"
              >
                None
              </Text>
            )}
          </div>
        )}
      </div>

      <div className="grow" />
      <div className="border-divider flex items-center justify-between border-t p-4">
        <span className="text-text-link flex items-center gap-1">
          <Text appearance="BODY2">More</Text>
          <LocalIcon
            name="openInNew"
            size="18"
          />
        </span>
        {onRemove && (
          <button
            className="text-text-secondary flex cursor-pointer items-center gap-1 border-none bg-transparent p-0"
            type="button"
            onClick={onRemove}
          >
            <Icon
              className="text-[18px]"
              children="delete"
            />
            <Text appearance="BODY2">Delete</Text>
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * Every lookup input, in the order a reviewer scans them. Values are read lazily so an
 * added `PatientPaCaseLookupQuery` field only needs one entry here to show up in the UI.
 */
const QUERY_FIELDS: Array<{
  label: string
  read: (query: apiClient.PatientPaCaseLookupQuery) => string | string[] | number | null | undefined
}> = [
  { label: 'Name', read: (q) => [q.first_name, q.last_name].filter(Boolean).join(' ') },
  { label: 'DOB', read: (q) => (q.date_of_birth ? formatDateDefault(q.date_of_birth) : null) },
  {
    label: 'Service Date',
    read: (q) => (q.date_of_service ? formatDateDefault(q.date_of_service) : null),
  },
  { label: 'Policy Number', read: (q) => q.policy_number },
  { label: 'Reference Token', read: (q) => q.reference_token },
  { label: 'CPT Codes', read: (q) => q.cpt_codes },
  { label: 'Extracted Test Name', read: (q) => q.extracted_test_name },
  { label: 'Test Names', read: (q) => q.test_names },
  { label: 'Test Names From CPT', read: (q) => q.test_names_from_cpt },
  { label: 'Test Names From Extracted', read: (q) => q.test_names_from_extracted },
  { label: 'Limit', read: (q) => q.limit },
]

function QuerySummary({ query }: { query: apiClient.PatientPaCaseLookupQuery }) {
  const fields = QUERY_FIELDS.map(({ label, read }) => ({ label, value: read(query) })).filter(
    (field): field is { label: string; value: string | string[] | number } =>
      Array.isArray(field.value) ? field.value.length > 0 : !!field.value,
  )

  if (fields.length === 0) return null

  return (
    <div className="flex items-center gap-1">
      <Text
        className="text-text-secondary"
        appearance="CAPTION"
      >
        Searched by {fields.length} {fields.length === 1 ? 'criterion' : 'criteria'}
      </Text>
      <InfoTip iconSize="16">
        <div className="grid max-w-[360px] grid-cols-[auto_1fr] items-start gap-x-4 gap-y-2 pr-6">
          {fields.map(({ label, value }) => (
            <Fragment key={label}>
              <Text
                className="text-text-secondary"
                appearance="BODY2"
              >
                {label}
              </Text>
              {Array.isArray(value) ? (
                <div className="flex flex-wrap gap-1">
                  {value.map((item) => (
                    <Chip
                      key={item}
                      size="xs"
                    >
                      {item}
                    </Chip>
                  ))}
                </div>
              ) : (
                <Text
                  appearance="BODY2"
                  copyable
                >
                  {value}
                </Text>
              )}
            </Fragment>
          ))}
        </div>
      </InfoTip>
    </div>
  )
}
